import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { classNames } from "../../../helpers/classNames";
import { fetchShopifyProducts, createShopifyCheckout } from "../../../services/shopifyService";
import useFetch from "../../../hooks/useFetch";
import Button from "../../../components/forms/Button";
import { useDispatch } from "react-redux";
import { resetStep } from "../../../redux/slice/stepSlice";
import { setCoverImages, setImages } from "../../../redux/slice/sketchSlice";
import { SOCKET_URL } from "../../../config/apiConfig";
import { updateBookOrderDate } from "../../../redux/slice/profileSlice"

const ChoosePlan = ({ fromHomePage = false }) => {
  const [planLoading, setPlanLoading] = useState(true)
  const [checkoutLoading, setCheckoutLoading] = useState(null); // Track which button is loading
  const dispatch = useDispatch();
  const myBooks = useSelector(state => state.profile.myBooks)
  const { coverImages = [], images = [] } = useSelector(state => state.sketch)
  const { activeBookId } = useSelector(state => state.stepFlow)
  const { session } = useSelector((state) => state?.authProvider);
  const userId = session?.user?._id;
  const role = session?.user?.role;

  // Socket listener for webhookSuccess
  useEffect(() => {
    let socket;

    if (userId && role === "user") {
      // Get token from localStorage or session
      const token = localStorage.getItem('auth_token') || session?.user?.accessToken;
      
      if (!token) {
        console.error('No token found for socket authentication');
        return;
      }

      socket = io(SOCKET_URL, {
        auth: {
          token,
          // userId: userId,
        },
        // transports: ['polling', 'websocket'],
      });

      socket.on('connect', () => {
        console.log('🔌 Socket connected in ChoosePlan:', socket.id);
      });

      socket.on('webhookSuccess', (data) => {
        console.log('✅ WEBHOOK SUCCESS RECEIVED:', data);
        if (data.bookId === activeBookId) {
          const allImages = [...coverImages, ...images].filter(img => !!img.AIUrl).map(img => img.AIUrl)

          dispatch(resetStep());
          dispatch(setCoverImages([]));
          dispatch(setImages([]));
          dispatch(updateBookOrderDate({ bookId: activeBookId }));

          localStorage.removeItem('selectedPlan');
          navigate("/thank-you", {
            state: { 
              orderId: data.orderDetails.orderId, 
              bookId: activeBookId,
              imageUrls: allImages,
              paymentIntent: { 
                ...data.orderDetails.payment, 
                total: data.orderDetails.total 
              },
            } 
          });
        }
      });

      socket.on('disconnect', () => {
        console.log('🔌 Socket disconnected from ChoosePlan');
      });

      socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message);
      });
    }

    return () => {
      if (socket) {
        socket.disconnect();
        console.log('🧹 Socket cleanup in ChoosePlan');
      }
    };
  }, [userId, role, session?.token, activeBookId]);

  // Shopify products fetch
  const { data: shopifyPlans } = useFetch({
    dependencies: ["shopifyProducts"],
    fetchFunction: async () => {
      try {
        const products = await fetchShopifyProducts();
        console.log('🛍️ Shopify Raw Products:', products);

        // Transform products - only first variant per product
        const allPlans = [];

        products.forEach((edge, productIndex) => {
          const product = edge.node;

          // Only process the first variant
          const firstVariant = product.variants.edges[0];
          if (!firstVariant) return; // Skip if no variants

          const variant = firstVariant.node;
          const price = parseFloat(variant.price.amount);

          // Determine if this product should be highlighted
          // Highlighting the middle product in the array
          const isHighlighted = productIndex === Math.floor(products.length / 2);

          allPlans.push({
            id: variant.id,
            productId: product.id,
            name: product.title, // Always use product title
            description: product.description,
            highLightTag: isHighlighted ? 1 : 0,
            highLightText: "Most Popular",
            planPricingData: {
              price: price,
              priceDescription: `Select Plan`,
            },
            features: [],
            shopifyProductId: product.id,
            shopifyVariantId: variant.id,
            image: product.images.edges[0]?.node?.url || null,
          });
        });

        console.log('✅ Transformed Plans:', allPlans);
        return allPlans;
      } catch (error) {
        console.error('❌ Error:', error);
        return [];
      } finally {
        setPlanLoading(false)
      }
    },
  });

  const navigate = useNavigate();

  const handleSelectPlan = async (planObj) => {
    // Check authentication
    if (!userId || role !== "user" || fromHomePage) {
      localStorage.setItem('selectedPlan', JSON.stringify(planObj))
      window.dispatchEvent(new Event("cartUpdated"));
      if (!!userId) {
        window.location.href = '/create-book?stepCode=Create_New_Book'
      } else {
        navigate(`/signin?selectedPlanId=${encodeURIComponent(planObj.id)}`);
      }
      
      return;
    }

    setCheckoutLoading(planObj.id); // Show loading for this specific button

    try {
      // TODO: Replace with actual bookId from your MongoDB
      // Where do you store the book customization data?
      const bookId = activeBookId;
      const bookData = myBooks.find(({ _id }) => activeBookId === _id);

      console.log('📦 Creating checkout for:', {
        plan: planObj.name,
        variantId: planObj.shopifyVariantId,
        bookId,
      });

      // Create checkout and get URL
      const checkoutUrl = await createShopifyCheckout(
        planObj.shopifyVariantId,
        bookId,
        bookData.title,
        bookData.coverImage,
        planObj.name
      );

      console.log('✅ Redirecting to checkout:', checkoutUrl);

      // Redirect to Shopify checkout
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('❌ Checkout creation failed:', error);
      alert('Something went wrong creating checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const plans = shopifyPlans || [];

  return (
    <section className="relative py-16 bg-md-pink-light-2 min-h-[calc(100vh-20rem)]">
      <div className="relative w-full px-0 sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl 3xl:max-w-8xl mx-auto">
        <div className="space-y-8 sm:space-y-16 grid">
          <div className="space-y-6 text-center">
            <h2 className="text-4xl xl:text-5xl 3xl:text-6xl font-semibold tracking-tight">Pricing Plans</h2>
            <div className="text-lg lg:text-xl xl:text-2xl 3xl:text-3xl tracking-wide text-neutral-500 max-w-xl lg:max-w-3xl 3xl:max-w-5xl mx-auto leading-snug">
              Choose the perfect plan that fits your needs. All plans offer high-quality printing and beautiful results.
            </div>
          </div>

          {planLoading && (
            <div className="text-center text-neutral-500 py-12">
              <div className="inline-flex items-center gap-3">
                <i className="fa-solid fa-spinner fa-spin text-2xl text-md-pink"></i>
                <span className="text-lg">Loading plans...</span>
              </div>
            </div>
          )}

          {plans?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
              {plans?.map((item, itemindex) => (
                <div
                  key={itemindex}
                  className="relative bg-white p-8 transition-all duration-300 rounded-2xl border-2 border-transparent hover:border-md-pink hover:-translate-y-2.5 overflow-hidden"
                  style={{ boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.07)" }}
                >
                  {/* Diagonal Ribbon Badge */}
                  {item?.highLightTag === 1 && (
                    <div className="absolute top-0 right-0 overflow-hidden w-32 h-32 pointer-events-none">
                      <div className="absolute top-7 -right-9 w-40 bg-md-pink text-white text-center text-xs font-semibold py-1.5 rotate-45 shadow-md tracking-wide">
                        {item?.highLightText || "Most Popular"}
                      </div>
                    </div>
                  )}

                  <div className="space-y-6 lg:space-y-8 mb-6 lg:mb-8 w-full max-w-72 3xl:max-w-xs mx-auto">
                    <div className="text-center space-y-4">
                      <div className="text-[19.25px] text-[#1f2937] font-semibold capitalize">
                        {item?.name}
                      </div>
                      {typeof item?.planPricingData?.price === "number" &&
                        (() => {
                          const price = item?.planPricingData?.price.toFixed(2);
                          const [integer] = price.split(".");
                          return (
                            <div className="text-neutral-500 text-lg lg:text-xl xl:text-xl inline-flex items-end gap-2">
                              <span className="text-[27.5px] font-bold text-[#1f2937] mt-[5.5px] mb-[2.75px]">
                                {"$" + integer}
                              </span>
                            </div>
                          );
                        })()}
                    </div>

                    <div className="relative flex-shrink-0">
                      {item?.features?.length > 0 && (
                        <div className="space-y-2 lg:space-y-3">
                          {item?.features?.map((featureitem, featureindex) => (
                            <div key={featureindex} className="flex items-start text-base lg:text-base xl:text-lg text-neutral-500 gap-2 xl:gap-3">
                              <span className="text-xl xl:text-xl text-md-pink">
                                <i className="fa-light fa-fw fa-check"></i>
                              </span>
                              <span>{featureitem?.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto flex justify-center w-full max-w-72 3xl:max-w-xs mx-auto">
                    <Button
                      buttonHasLink={false}
                      buttonLabel={
                        checkoutLoading === item.id
                          ? "Processing..."
                          : item?.planPricingData?.priceDescription
                      }
                      buttonFunction={() => handleSelectPlan(item)}
                      buttonClasses={classNames(
                        "w-full !p-[11px] !h-auto transition-all !rounded-lg bg-transparent border-2 border-md-pink group-hover:!bg-md-pink-light-2 group",
                        item?.highLightTag === 1
                          ? ""
                          : "bg-md-pink-light-1 !text-md-pink !font-medium",
                        checkoutLoading !== null && "!cursor-not-allowed"
                      )}
                      buttonLabelClasses={classNames(
                        "!text-[12.375px] !font-semibold text-md-pink capitalize",
                        "!transition-colors !duration-200",
                        "group-hover:!text-white"
                      )}
                      isDisable={!!checkoutLoading}
                    >
                      {checkoutLoading === item.id && (
                        <i className="fa-solid fa-spinner fa-spin"></i>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ChoosePlan;